import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiSave, FiArrowRight, FiSettings, FiCheckCircle, FiXCircle, FiHelpCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { flujoAPI } from '../utils/api';

const ConfiguracionFlujo = () => {
  const [configuraciones, setConfiguraciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mostrarAyuda, setMostrarAyuda] = useState(true);
  const { hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!hasRole('administrador')) {
      navigate('/');
      return;
    }
    cargarConfiguraciones();
  }, []);

  const cargarConfiguraciones = async () => {
    try {
      setLoading(true);
      const response = await flujoAPI.listarConfiguraciones();
      setConfiguraciones(response.data.data || []);
    } catch (error) {
      console.error('Error al cargar configuraciones:', error);
      alert('Error al cargar configuraciones del flujo. Por favor, verifica la consola.');
    } finally {
      setLoading(false);
    }
  };

  const toggleActivo = async (config) => {
    try {
      await flujoAPI.actualizarConfiguracion(config.id, { 
        activo: !config.activo 
      });
      cargarConfiguraciones();
    } catch (error) {
      console.error('Error al actualizar configuración:', error);
      alert('Error al actualizar la configuración');
    }
  };

  const getEstadoColor = (estado) => {
    const colores = {
      'pendiente': 'bg-gray-100 text-gray-800',
      'creada_gestor': 'bg-blue-100 text-blue-800',
      'informacion_valida': 'bg-purple-100 text-purple-800',
      'informacion_contrastada_editor': 'bg-indigo-100 text-indigo-800',
      'informacion_aprobada': 'bg-green-100 text-green-800',
      'rechazada': 'bg-red-100 text-red-800'
    };
    return colores[estado] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold mb-2 flex items-center">
                <FiSettings className="mr-3" />
                Configuración del Flujo de Proceso
              </h1>
              <p className="text-blue-100">
                Administra las transiciones del flujo de trabajo Gestor → Validador → Editor
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Panel de Ayuda y Tutorial */}
        {mostrarAyuda && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-6 mb-8 shadow-md">
            <div className="flex items-start justify-between mb-4">
              <h2 className="text-2xl font-bold text-blue-900 flex items-center">
                <FiHelpCircle className="mr-3" size={28} />
                📖 Guía de Configuración del Flujo
              </h2>
              <button
                onClick={() => setMostrarAyuda(false)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Ocultar
              </button>
            </div>

            {/* ¿Qué Puedes Configurar? */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-blue-900 mb-3 flex items-center">
                <FiSettings className="mr-2" />
                ¿Qué puedes configurar?
              </h3>
              <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
                <div className="flex items-start">
                  <FiCheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Activar/Desactivar Transiciones:</strong> Habilita o deshabilita rutas específicas del flujo sin borrar la configuración.</p>
                </div>
                <div className="flex items-start">
                  <FiCheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Requiere Retroalimentación:</strong> Determina si el usuario debe dar una razón obligatoria al hacer la transición.</p>
                </div>
                <div className="flex items-start">
                  <FiCheckCircle className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Descripción:</strong> Documenta el propósito de cada transición para facilitar el mantenimiento.</p>
                </div>
              </div>
            </div>

            {/* ¿Qué NO Puedes Configurar? */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center">
                <FiAlertTriangle className="mr-2" />
                ¿Qué NO puedes configurar?
              </h3>
              <div className="bg-white rounded-lg p-4 space-y-2 text-sm">
                <div className="flex items-start">
                  <FiXCircle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Estados de Origen y Destino:</strong> No se pueden cambiar los estados involucrados en la transición (requiere migración de base de datos).</p>
                </div>
                <div className="flex items-start">
                  <FiXCircle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Roles:</strong> No se pueden modificar qué roles ejecutan cada transición (definidos en el modelo de datos).</p>
                </div>
                <div className="flex items-start">
                  <FiXCircle className="text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                  <p><strong>Crear/Eliminar Transiciones:</strong> No se pueden agregar o eliminar transiciones desde esta pantalla.</p>
                </div>
              </div>
            </div>

            {/* Tutorial Paso a Paso */}
            <div className="mb-6">
              <h3 className="text-lg font-bold text-indigo-900 mb-3 flex items-center">
                <FiInfo className="mr-2" />
                Tutorial: Cómo Activar/Desactivar una Transición
              </h3>
              <div className="bg-white rounded-lg p-4">
                <ol className="list-decimal list-inside space-y-3 text-sm">
                  <li>
                    <strong>Identifica la transición:</strong> En la sección "Configuración de Transiciones", encuentra la tarjeta que representa la transición que quieres modificar.
                    <div className="ml-6 mt-1 text-gray-600">
                      Ejemplo: <span className="font-mono bg-gray-100 px-2 py-0.5 rounded">creada_gestor → informacion_valida</span>
                    </div>
                  </li>
                  <li>
                    <strong>Revisa el estado actual:</strong> Las transiciones activas tienen borde verde y fondo verde claro. Las desactivadas tienen borde gris y están atenuadas.
                  </li>
                  <li>
                    <strong>Haz clic en el botón de estado:</strong> Verás un botón a la derecha que dice "Activa" (verde) o "Desactivada" (gris).
                  </li>
                  <li>
                    <strong>Confirma el cambio:</strong> Al hacer clic, el estado cambiará inmediatamente y el sistema actualizará la configuración.
                  </li>
                  <li>
                    <strong>Verifica el impacto:</strong> Asegúrate de que los usuarios puedan completar sus tareas sin bloquearse en un estado.
                  </li>
                </ol>
              </div>
            </div>

            {/* Advertencias Importantes */}
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4">
              <h4 className="font-bold text-red-900 mb-2 flex items-center">
                <FiAlertTriangle className="mr-2" />
                ⚠️ Advertencias Importantes
              </h4>
              <ul className="space-y-1 text-sm text-red-800">
                <li>• <strong>Bloqueos:</strong> Desactivar transiciones clave puede dejar registros atrapados sin poder avanzar.</li>
                <li>• <strong>Flujo Completo:</strong> Asegúrate de que siempre exista al menos un camino desde Gestor hasta Publicado.</li>
                <li>• <strong>Estados Pendiente/Rechazado:</strong> Mantén activas las transiciones hacia estos estados para evitar bloqueos.</li>
                <li>• <strong>Usuarios Afectados:</strong> Los cambios afectan inmediatamente a todos los usuarios del sistema.</li>
              </ul>
            </div>
          </div>
        )}

        {!mostrarAyuda && (
          <button
            onClick={() => setMostrarAyuda(true)}
            className="mb-4 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition font-medium flex items-center"
          >
            <FiHelpCircle className="mr-2" />
            Mostrar Guía de Ayuda
          </button>
        )}

        {/* Gráfico del Flujo */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Diagrama del Flujo de Trabajo</h2>
          
          <div className="flex flex-col space-y-4">
            {/* Nivel 1: Gestor */}
            <div className="flex items-center justify-center">
              <div className="bg-blue-100 border-2 border-blue-500 rounded-lg p-4 w-64 text-center">
                <div className="text-sm font-semibold text-blue-600 mb-1">ROL: GESTOR</div>
                <div className="text-lg font-bold text-gray-900">Borrador / Creación</div>
                <div className="text-xs text-gray-600 mt-2">
                  Estados: <span className="font-mono">pendiente, creada_gestor</span>
                </div>
              </div>
            </div>

            {/* Flecha hacia abajo */}
            <div className="flex justify-center">
              <FiArrowRight className="text-green-500 transform rotate-90" size={32} />
            </div>

            {/* Nivel 2: Validador */}
            <div className="flex items-center justify-center">
              <div className="bg-purple-100 border-2 border-purple-500 rounded-lg p-4 w-64 text-center">
                <div className="text-sm font-semibold text-purple-600 mb-1">ROL: VALIDADOR</div>
                <div className="text-lg font-bold text-gray-900">Validación</div>
                <div className="text-xs text-gray-600 mt-2">
                  Estado: <span className="font-mono">informacion_valida</span>
                </div>
              </div>
            </div>

            {/* Flecha hacia abajo */}
            <div className="flex justify-center">
              <FiArrowRight className="text-green-500 transform rotate-90" size={32} />
            </div>

            {/* Nivel 3: Editor */}
            <div className="flex items-center justify-center">
              <div className="bg-green-100 border-2 border-green-500 rounded-lg p-4 w-64 text-center">
                <div className="text-sm font-semibold text-green-600 mb-1">ROL: EDITOR</div>
                <div className="text-lg font-bold text-gray-900">Revisión Final</div>
                <div className="text-xs text-gray-600 mt-2">
                  Estado: <span className="font-mono">informacion_contrastada_editor</span>
                </div>
              </div>
            </div>

            {/* Flecha hacia abajo */}
            <div className="flex justify-center">
              <FiArrowRight className="text-green-500 transform rotate-90" size={32} />
            </div>

            {/* Nivel 4: Publicado */}
            <div className="flex items-center justify-center">
              <div className="bg-green-500 border-2 border-green-700 rounded-lg p-4 w-64 text-center text-white">
                <div className="text-sm font-semibold mb-1">ESTADO FINAL</div>
                <div className="text-lg font-bold">✅ Publicado</div>
                <div className="text-xs mt-2">
                  Estado: <span className="font-mono">informacion_aprobada</span>
                </div>
              </div>
            </div>

            {/* Estados Alternativos */}
            <div className="mt-8 pt-8 border-t-2 border-gray-200">
              <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">Estados Alternativos</h3>
              <div className="flex justify-center gap-4">
                <div className="bg-orange-100 border-2 border-orange-500 rounded-lg p-3 w-48 text-center">
                  <div className="text-sm font-semibold text-orange-600">PENDIENTE</div>
                  <div className="text-xs text-gray-600 mt-1">En espera de información</div>
                </div>
                <div className="bg-red-100 border-2 border-red-500 rounded-lg p-3 w-48 text-center">
                  <div className="text-sm font-semibold text-red-600">RECHAZADO</div>
                  <div className="text-xs text-gray-600 mt-1">No cumple criterios</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Configuraciones de Transiciones */}
        <div className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Configuración de Transiciones</h2>
          
          <div className="space-y-4">
            {configuraciones.map((config) => (
              <div 
                key={config.id} 
                className={`border-2 rounded-lg p-4 transition-all ${
                  config.activo 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-300 bg-gray-50 opacity-60'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getEstadoColor(config.estadoOrigen)}`}>
                        {config.estadoOrigen}
                      </span>
                      <FiArrowRight className="text-gray-400" size={20} />
                      <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getEstadoColor(config.estadoDestino)}`}>
                        {config.estadoDestino}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600">
                      <strong>De:</strong> {config.rolOrigen} → <strong>A:</strong> {config.rolDestino || 'Mismo rol'}
                    </div>
                    {config.descripcion && (
                      <p className="text-sm text-gray-500 mt-1">{config.descripcion}</p>
                    )}
                    {config.requiereRetroalimentacion && (
                      <span className="inline-block mt-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded">
                        Requiere retroalimentación
                      </span>
                    )}
                  </div>
                  <div className="ml-4">
                    <button
                      onClick={() => toggleActivo(config)}
                      className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition ${
                        config.activo
                          ? 'bg-green-600 text-white hover:bg-green-700'
                          : 'bg-gray-400 text-white hover:bg-gray-500'
                      }`}
                    >
                      {config.activo ? (
                        <>
                          <FiCheckCircle />
                          <span>Activa</span>
                        </>
                      ) : (
                        <>
                          <FiXCircle />
                          <span>Desactivada</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {configuraciones.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <p>No hay configuraciones de flujo disponibles.</p>
              <p className="text-sm mt-2">Las configuraciones se crean automáticamente con las migraciones.</p>
            </div>
          )}
        </div>

        {/* Información Adicional */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mt-6">
          <h3 className="text-lg font-bold text-blue-900 mb-3">ℹ️ Información Importante</h3>
          <ul className="space-y-2 text-sm text-blue-800">
            <li>• <strong>Transiciones Activas</strong>: Los usuarios pueden realizar esta transición en el flujo</li>
            <li>• <strong>Transiciones Desactivadas</strong>: Bloquean temporalmente esta opción sin eliminar la configuración</li>
            <li>• <strong>Cuidado</strong>: Desactivar transiciones puede bloquear registros en ciertos estados</li>
            <li>• <strong>Recomendación</strong>: Solo desactiva transiciones si estás seguro del impacto</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default ConfiguracionFlujo;

